import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Logout, User } from '@carbon/icons-react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../utils/axiosInstance';
import LogoutModal from './LogoutModal';
import './AccountMenu.css';

/**
 * Ime i prezime prijavljenog korisnika iz JWT tokena.
 * Token koristi Microsoft "claims", pa se ime čita iz više mogućih polja
 * (redosled od najverodostojnijeg ka rezervnom).
 */
const nameFromToken = (user) => {
  if (!user) return '';
  const claimName =
    user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || '';
  const given = user.given_name || '';
  const family = user.family_name || '';
  return (
    user.fullName ||
    user.name ||
    `${given} ${family}`.trim() ||
    claimName ||
    user.unique_name ||
    user.preferred_username ||
    user.email ||
    ''
  );
};

/**
 * AccountMenu - u gornjem desnom uglu (PC) prikazuje "Ime Prezime" korisnika i
 * strelicu nadole; klikom otvara padajući meni sa stavkama "Moj nalog" i
 * "Odjavi se".
 */
const AccountMenu = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(() => nameFromToken(user));
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const containerRef = useRef(null);

  // Ako se token/korisnik promeni (npr. posle osvežavanja), osveži i prikazano ime
  useEffect(() => {
    setFullName((prev) => prev || nameFromToken(user));
  }, [user]);

  // Tačno ime i prezime dolazi sa servera (isti endpoint koji koristi "Moj nalog").
  // Ako iz bilo kog razloga ne uspe, ostaje ime iz tokena.
  //
  // VAŽNO: ne pozivati API dok AuthContext ne razreši sesiju. Shell (a samim tim
  // i ovaj meni) se renderuje i tokom tihog refresh-a, pa bi zahtev otišao bez
  // Authorization headera, dobio 401 i pokrenuo nepotreban refresh/logout tok.
  useEffect(() => {
    if (loading || !user) return undefined;

    let isMounted = true;

    const fetchMe = async () => {
      try {
        const response = await axiosInstance.get('/api/Employee/me');
        const data = response.data?.data || response.data;
        const name =
          data?.fullName || `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
        if (isMounted && name) setFullName(name);
      } catch (err) {
        // Bez imena sa servera nije greška za korisnika - koristi se ime iz tokena
        console.error('Greška pri učitavanju imena korisnika:', err);
      }
    };

    fetchMe();
    return () => {
      isMounted = false;
    };
  }, [loading, user]);

  // Zatvaranje na klik van menija ili na taster Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Odjava uz potvrdu (isti tok kao na stranici "Moj nalog")
  const handleLogout = async () => {
    setIsLogoutModalOpen(false);
    try {
      await logout();
    } catch (err) {
      console.error('Greška pri odjavi:', err);
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const displayName = fullName || 'Moj nalog';

  return (
    <div className="account-menu" ref={containerRef}>
      <button
        type="button"
        className="account-menu__trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={displayName}
      >
        <span className="account-menu__name">{displayName}</span>
        <ChevronDown
          size={16}
          className={`account-menu__chevron ${isOpen ? 'is-open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="account-menu__dropdown" role="menu">
          <Link
            to="/profile"
            className="account-menu__item"
            role="menuitem"
            onClick={() => setIsOpen(false)}
          >
            <User size={16} />
            Moj nalog
          </Link>
          <button
            type="button"
            className="account-menu__item account-menu__item--logout"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              setIsLogoutModalOpen(true);
            }}
          >
            <Logout size={16} />
            Odjavi se
          </button>
        </div>
      )}

      {/* Potvrda odjave (isti modal kao na stranici "Moj nalog") */}
      <LogoutModal
        open={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default AccountMenu;
